'use client';

import Link from 'next/link';
import { useRouter } from 'next/navigation';

const FEATURED_VILLAS = [
  { name: 'Castello di Tuscany', location: 'Tuscany, Italy', desc: '12-bedroom private castle with vineyard, pool and full staff', price: 'From $78,000 per week', tags: ['Castle', 'Vineyard', 'Italy'] },
  { name: 'Bali Cliff-Top Estate', location: 'Bali, Indonesia', desc: '8-bedroom Uluwatu compound with infinity pool and ocean views', price: 'From $24,000 per week', tags: ['Cliff-Top', 'Bali', 'Infinity Pool'] },
  { name: 'Cotswolds Manor House', location: 'Cotswolds, UK', desc: '10-bedroom country estate with gardens, tennis court and chef', price: 'From $38,000 per week', tags: ['Manor', 'UK', 'Country Estate'] },
  { name: 'St Barths Beachfront Villa', location: 'St Barths, Caribbean', desc: '6-bedroom oceanfront villa with private beach and boat', price: 'From $42,000 per week', tags: ['Beachfront', 'Caribbean', 'Private Beach'] },
];

const FEATURES = [
  { icon: '👨‍🍳', title: 'Fully staffed', desc: 'Butler, chef, housekeeping and concierge included at every property.' },
  { icon: '🏰', title: 'Exclusive-use', desc: 'The entire estate — pool, staff, grounds — is yours alone.' },
  { icon: '🌟', title: 'Villa concierge', desc: 'In-villa dining, spa therapists, private tours arranged nightly.' },
];

const DESTINATIONS = [
  { name: 'Tuscany, Italy', icon: '🇮🇹', villas: '24 villas' },
  { name: 'Amalfi Coast, Italy', icon: '🇮🇹', villas: '18 villas' },
  { name: 'St Barths, Caribbean', icon: '🏝', villas: '32 villas' },
  { name: 'Bali, Indonesia', icon: '🇮🇩', villas: '45 villas' },
  { name: 'Mykonos, Greece', icon: '🇬🇷', villas: '28 villas' },
  { name: 'Cotswolds, UK', icon: '🇬🇧', villas: '15 villas' },
  { name: 'Provence, France', icon: '🇫🇷', villas: '22 villas' },
  { name: 'Marbella, Spain', icon: '🇪🇸', villas: '19 villas' },
];

export default function VillasPage() {
  const router = useRouter();

  return (
    <div className="min-h-screen bg-[var(--background)] text-[var(--foreground)]">
      {/* Nav */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[var(--surface-2)]/95 backdrop-blur-xl border-b border-[var(--border)] h-16 flex items-center px-6">
        <Link href="/" className="text-[var(--primary)] font-bold tracking-widest uppercase mr-8" style={{ fontFamily: 'var(--font-serif)' }}>WorldwayLuxe</Link>
        <div className="flex items-center gap-6 text-sm text-[var(--muted-foreground)]">
          <Link href="/hotels" className="hover:text-[var(--primary)] transition-colors">Hotels</Link>
          <Link href="/yachts" className="hover:text-[var(--primary)] transition-colors">Yacht Charters</Link>
          <Link href="/tailor-made" className="hover:text-[var(--primary)] transition-colors">Tailor-Made</Link>
        </div>
        <div className="ml-auto flex items-center gap-3">
          <Link href="/auth/login" className="text-sm text-[var(--muted-foreground)] hover:text-[var(--foreground)] px-4 py-2">Sign In</Link>
          <Link href="/auth/register" className="btn-primary text-sm px-5 py-2 rounded-lg">Join Now</Link>
        </div>
      </nav>
      {/* Hero */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#050810] via-[var(--background)] to-[#0D1525]" />
        <div className="absolute inset-0 opacity-20" style={{ backgroundImage: 'radial-gradient(ellipse 80% 60% at 50% 20%, rgba(201,168,76,0.15) 0%, transparent 70%)' }} />
        <div className="relative z-10 max-w-5xl mx-auto px-6">
          <p className="label-caps mb-3 text-[var(--primary)]">Whole-house rentals</p>
          <h1 className="text-4xl md:text-6xl font-bold mb-4" style={{ fontFamily: 'var(--font-serif)' }}>
            Villas & Estates<br />
            <span className="gold-text-gradient">Entirely Yours.</span>
          </h1>
          <p className="text-lg text-[var(--muted-foreground)] mb-6 max-w-xl">
            Fully staffed private villas, historic estates and beachfront residences worldwide — from Tuscan castellos to St. Barths beachfront to Balinese cliff-top compounds.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10">
            {FEATURES?.map((f) => (
              <div key={f?.title} className="glass-card rounded-xl p-4 border border-[rgba(201,168,76,0.1)]">
                <div className="text-2xl mb-2">{f?.icon}</div>
                <div className="text-sm font-bold mb-1">{f?.title}</div>
                <div className="text-xs text-[var(--muted-foreground)]">{f?.desc}</div>
              </div>
            ))}
          </div>
          <div className="flex gap-4">
            <button onClick={() => router?.push('/booking?vertical=villa')} className="btn-primary px-8 py-3 rounded-xl text-sm font-semibold">Browse Villas</button>
            <Link href="/ai-concierge" className="btn-ghost px-8 py-3 rounded-xl text-sm font-semibold border border-[var(--border)]">Ask Concierge</Link>
          </div>
        </div>
      </section>
      {/* Featured Villas */}
      <section className="py-16 border-t border-[var(--border)]">
        <div className="max-w-5xl mx-auto px-6">
          <p className="label-caps mb-2">Featured Properties</p>
          <h2 className="text-2xl font-bold mb-8" style={{ fontFamily: 'var(--font-serif)' }}>Handpicked <span className="gold-text-gradient">Luxury Estates</span></h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {FEATURED_VILLAS?.map((v) => (
              <div key={v?.name} className="glass-card rounded-xl p-5 hover:border-[rgba(201,168,76,0.3)] transition-all group cursor-pointer" onClick={() => router?.push('/booking?vertical=villa')}>
                <div className="text-2xl mb-3">🏰</div>
                <h3 className="text-base font-bold mb-1 group-hover:text-[var(--primary)] transition-colors" style={{ fontFamily: 'var(--font-serif)' }}>{v?.name}</h3>
                <p className="text-xs text-[var(--muted-foreground)] mb-1">{v?.location}</p>
                <p className="text-xs text-[var(--muted-foreground)] mb-3 leading-relaxed">{v?.desc}</p>
                <div className="flex flex-wrap gap-1 mb-3">
                  {v?.tags?.map((tag) => (
                    <span key={tag} className="text-[10px] px-2 py-0.5 rounded-full bg-[rgba(201,168,76,0.1)] text-[var(--primary)]">{tag}</span>
                  ))}
                </div>
                <p className="text-xs font-semibold text-[var(--primary)]">{v?.price}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* Destinations */}
      <section className="py-16 border-t border-[var(--border)]">
        <div className="max-w-5xl mx-auto px-6">
          <p className="label-caps mb-2">Villa Destinations</p>
          <h2 className="text-2xl font-bold mb-8" style={{ fontFamily: 'var(--font-serif)' }}>Where to <span className="gold-text-gradient">Stay in Style</span></h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {DESTINATIONS?.map((d) => (
              <button key={d?.name} onClick={() => router?.push(`/booking?vertical=villa&destination=${d?.name}`)} className="glass-card rounded-xl p-4 text-center hover:border-[rgba(201,168,76,0.3)] transition-all group">
                <div className="text-2xl mb-2">{d?.icon}</div>
                <div className="text-xs font-bold mb-1 group-hover:text-[var(--primary)] transition-colors">{d?.name}</div>
                <div className="text-[10px] text-[var(--muted-foreground)]">{d?.villas}</div>
              </button>
            ))}
          </div>
        </div>
      </section>
      {/* FAQ */}
      <section className="py-16 border-t border-[var(--border)]">
        <div className="max-w-5xl mx-auto px-6">
          <p className="label-caps mb-6">FAQ</p>
          <div className="space-y-4">
            {[
              { q: 'What is the minimum stay?', a: 'Typically 5 nights; 7 nights in peak season. Some properties have longer minimums during Christmas and New Year.' },
              { q: 'What staff is included?', a: 'Butler, chef, housekeeping and concierge are included at every property. Additional staff (nannies, security, personal trainers) can be arranged.' },
              { q: 'Can I arrange in-villa experiences?', a: 'Absolutely. In-villa dining, spa therapists, private tours, wine tastings and more can be arranged nightly by your villa concierge.' },
            ]?.map((faq) => (
              <div key={faq?.q} className="glass-card rounded-xl p-5">
                <div className="text-sm font-bold mb-2 text-[var(--primary)]">{faq?.q}</div>
                <div className="text-xs text-[var(--muted-foreground)] leading-relaxed">{faq?.a}</div>
              </div>
            ))}
          </div>
        </div>
      </section>
      <footer className="border-t border-[var(--border)] py-8">
        <div className="max-w-5xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="text-xs text-[var(--muted-foreground)]">© 2026 Worldway Travels Group · All rights reserved.</div>
          <div className="flex items-center gap-6 text-xs text-[var(--muted-foreground)]">
            <Link href="/" className="hover:text-[var(--primary)] transition-colors">Home</Link>
            <Link href="/yachts" className="hover:text-[var(--primary)] transition-colors">Yachts</Link>
            <Link href="/hotels" className="hover:text-[var(--primary)] transition-colors">Hotels</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
